import { addDays, addMinutes, differenceInSeconds, isPast, isValid, startOfMinute } from 'date-fns';
import type { FocusEvent, KeyboardEvent as ReactKeyboardEvent } from 'react';
import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import type { DayPickerProps } from 'react-day-picker';
import { useTheme } from 'styled-components';
import { FormControlSizes, type FormControlProps } from '../Form/types';

import { isEqual } from 'lodash-es';
import { useElementId, useNamedState } from '../../hooks';
import { formattedDate, isValidDateInput, logger, parseDate } from '../../utils';
import { Button, ButtonGroup, ButtonVariants, IconButton, ToggleButton } from '../Button';
import { Flex, HStack, VStack } from '../Core';
import { DEFAULT_DATE_PICKER_EOD } from '../DateTimePicker';
import { DayPicker } from '../DayPicker';
import { Input } from '../Form';
import { Icon, IconName } from '../Icons';
import { Popover } from '../Popover/Popover';
import { usePopoverState } from '../Popover/usePopoverState';
import { TimePicker } from '../TimePicker';
import { TimePickerPrecision } from '../TimePicker/TimePickerPrecision';
import {
  calcDateFromDuration,
  canParseAsDuration,
  emptyDuration,
  formatDuration,
  isDuration,
  parseDuration,
  type Duration,
} from './duration';
import { DEFAULT_DURATION_SHORTCUTS, DurationPicker } from './DurationPicker';
import {
  DEFAULT_ENTRY_MODES,
  DateTimeDurationPickerValueType,
  getIncomingValueStateSignature,
  getInitialEntryMode,
  resolveIncomingValueState,
  type DateTimeDurationPickerControlValue,
  type DateTimeDurationPickerEntryModes,
  type DateTimeDurationPickerValue,
} from './incomingValueState';
import {
  DateDescription,
  DateTimeDurationPickerWrapper,
  DateTimeWrapper,
  DurationDescription,
  InputOverlay,
  PopoverWrapper,
  TimePickerWrapper,
} from './styles';

export {
  calcDateFromDuration,
  canParseAsDuration,
  emptyDuration,
  formatDuration,
  formatDurationForDisplay,
  formatToBackendDuration,
  isDuration,
  isEmptyDuration,
  parseDuration,
} from './duration';
export type { Duration } from './duration';
export { DurationPicker } from './DurationPicker';
export {
  DateTimeDurationPickerValueType,
  getSafeEntryModes,
  isDateTimeDurationPickerValue,
} from './incomingValueState';
export type { DateTimeDurationPickerEntryModes, DateTimeDurationPickerValue } from './incomingValueState';

export interface DateTimeDurationPickerProps {
  /** Current value of the date / time picker */
  value: DateTimeDurationPickerControlValue;
  /** Change event handler */
  onChange: (value: DateTimeDurationPickerValue) => void;
  /** Show the shortcuts section (5m/10m/...)? */
  showShortcuts?: boolean;
  /** Shortcuts to show
   *
   * @default {@link DEFAULT_SHORTCUTS}
   */
  shortcuts?: { [key: string]: string };
  /** Label to use on the Date/Time toggle button
   *
   * @default Date/Time
   */
  dateTimeLabel?: string;
  /** Label to use on the Duration toggle button
   *
   * @default Duration
   */
  durationLabel?: string;
  calcDurationRelativeTo?: Date | null;
  /** Whether to show calculated duration as date */
  showCalculatedDuration?: boolean;
  disabled?: boolean;
  /** Time to use as End-Of-Day
   *
   * @default {@link DEFAULT_DATE_PICKER_EOD}
   */
  customEOD?: { hours: number; minutes: number };
  /** Portalize component: @default: true */
  portalize?: boolean;
  initialEntryMode?: DateTimeDurationPickerValueType;

  entryModes?: DateTimeDurationPickerEntryModes;
  dataTestId?: string;
}

type ForwardedProps = Omit<FormControlProps<HTMLInputElement>, keyof DateTimeDurationPickerProps | 'prefix'>;

/**
 * Date / Time Picker component
 */
export const DateTimeDurationPicker = memo(function DateTimeDurationPicker({
  value,
  shortcuts = DEFAULT_DURATION_SHORTCUTS,
  showShortcuts = true,
  onChange,
  dateTimeLabel = 'Date/Time',
  durationLabel = 'Duration',
  customEOD = DEFAULT_DATE_PICKER_EOD,
  calcDurationRelativeTo,
  showCalculatedDuration = true,
  disabled,
  portalize = true,
  size,
  initialEntryMode,
  dataTestId,
  entryModes = DEFAULT_ENTRY_MODES,
  id,
  invalid,
  warning,
  touched,
  ...props
}: DateTimeDurationPickerProps & ForwardedProps) {
  const { onKeyDown: onKeyDownProp } = props;
  const theme = useTheme();
  const idGenerator = useElementId(id ?? 'DateTimeDurationPicker');
  const [isFocused, setIsFocused] = useNamedState(false, 'isFocused');
  const [entryMode, setEntryMode] = useNamedState(
    getInitialEntryMode({
      initialEntryMode,
      value,
      entryModes,
    }),
    'entryMode'
  );
  const [inputValue, setInputValue] = useNamedState<string>('', 'inputValue');
  const [internalTouched, setInternalTouched] = useNamedState<boolean>(false, 'internalTouched');
  const [selectedDuration, _setSelectedDuration] = useNamedState<Duration>(
    isDuration(value) ? value : emptyDuration,
    'selectedDuration'
  );
  const [selectedDate, _setSelectedDate] = useNamedState<Date | null>(
    isDuration(value) || !isValidDateInput(value) || value === null ? null : parseDate(value),
    'selectedDate'
  );
  const inputRef = useRef<HTMLInputElement>(null);
  const clearButtonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [selectedTime, setSelectedTime] = useNamedState<Date>(
    selectedDate ? parseDate(selectedDate) : parseDate(),
    'selectedTime'
  );
  const isFocusOutsideComponent = useCallback(
    (newTarget: EventTarget | null) => {
      if (inputRef.current?.contains(newTarget as Node | null)) {
        return false;
      }
      if (popoverRef.current?.contains(newTarget as Node | null)) {
        return false;
      }
      return true;
    },
    [inputRef, popoverRef]
  );
  const popover = usePopoverState({
    trigger: '',
    placement: 'bottom-end',
    modifiers: [
      {
        name: 'flip',
        options: {
          fallbackPlacements: ['top', 'bottom'],
        },
      },
    ],
    delay: undefined,
    usePortal: portalize,
    onClickOutside: e => {
      if (isFocusOutsideComponent(e.target)) {
        if (internalTouched) {
          // if the user has change the value at all, then we'll act as if they clicked "Set" when they blur the control
          handleClickSet();
        } else {
          // Otherwise, if they made no changes, act as if they click cancel
          handleClickCancel();
        }
      }
    },
    onClose: () => {
      setIsFocused(false);
    },
  });
  const { open: openPopup, close: closePopup } = popover;

  const handleFocus = useCallback(
    (e: FocusEvent<HTMLElement>) => {
      const target = e.target as HTMLElement | null;
      // Clearing a value should not immediately reopen the popover.
      if (target && clearButtonRef.current?.contains(target)) {
        return;
      }

      if (!isFocused) {
        if (entryMode === DateTimeDurationPickerValueType.DateTime) {
          let newSelectedTime = selectedDate ? parseDate(selectedDate) : parseDate();
          if (!selectedDate) {
            // if we don't have a value yet, default the time to the start of the next minute
            newSelectedTime = addMinutes(startOfMinute(newSelectedTime), 1);
            if (differenceInSeconds(newSelectedTime, parseDate()) < 10) {
              // ensure our default time is at least 10 seconds from now
              newSelectedTime = addMinutes(newSelectedTime, 1);
            }
          }
          setSelectedTime(newSelectedTime);
        }
      }

      setIsFocused(true);
      openPopup();
    },
    [entryMode, isFocused, openPopup, selectedDate, setSelectedTime, setIsFocused]
  );
  const hasDateTimeEntryMode = entryModes.includes(DateTimeDurationPickerValueType.DateTime);
  const hasDurationEntryMode = entryModes.includes(DateTimeDurationPickerValueType.Duration);
  const lastSyncedIncomingStateRef = useRef<string | null>(null);
  const { resolvedIncomingValueState, incomingValueStateSignature } = useMemo(() => {
    const incomingValueContext = {
      value,
      hasDateTimeEntryMode,
      hasDurationEntryMode,
    };

    return {
      resolvedIncomingValueState: resolveIncomingValueState(incomingValueContext),
      incomingValueStateSignature: getIncomingValueStateSignature(incomingValueContext),
    };
  }, [value, hasDateTimeEntryMode, hasDurationEntryMode]);

  // Sync incoming controlled value to local state.
  // Use a semantic signature so equivalent value rerenders do not clobber in-progress edits.
  useEffect(() => {
    // Respect real priming updates even while focused, but ignore equivalent rerenders.
    if (lastSyncedIncomingStateRef.current === incomingValueStateSignature) {
      return;
    }
    lastSyncedIncomingStateRef.current = incomingValueStateSignature;

    switch (resolvedIncomingValueState.kind) {
      case 'duration':
        // Supported Duration value: render it as-is and stay in Duration mode.
        setInputValue(formatDuration(resolvedIncomingValueState.duration));
        setEntryMode(DateTimeDurationPickerValueType.Duration);
        _setSelectedDuration(resolvedIncomingValueState.duration);
        _setSelectedDate(null);
        break;
      case 'dateTime':
        // Supported DateTime value: render it as-is and stay in DateTime mode.
        setInputValue(formattedDate(resolvedIncomingValueState.dateTime));
        setEntryMode(DateTimeDurationPickerValueType.DateTime);
        _setSelectedDuration(emptyDuration);
        _setSelectedDate(resolvedIncomingValueState.dateTime);
        setSelectedTime(resolvedIncomingValueState.dateTime);
        break;
      case 'sanitize':
        // Unsupported or empty incoming value: clear local value state to avoid mixed-mode UI.
        setInputValue('');
        _setSelectedDuration(emptyDuration);
        _setSelectedDate(null);
        if (resolvedIncomingValueState.fallbackEntryMode != null) {
          setEntryMode(resolvedIncomingValueState.fallbackEntryMode);
        }
        break;
    }

    setInternalTouched(false);
  }, [
    resolvedIncomingValueState,
    incomingValueStateSignature,
    setInputValue,
    _setSelectedDuration,
    _setSelectedDate,
    setSelectedTime,
    setInternalTouched,
    setEntryMode,
  ]);

  const setDateTimeEntryMode = useCallback(() => {
    setEntryMode(DateTimeDurationPickerValueType.DateTime);
    setInputValue(isValidDateInput(selectedTime) ? formattedDate(selectedTime) : '');
  }, [selectedTime, setEntryMode, setInputValue]);
  const setDurationEntryMode = useCallback(() => {
    setEntryMode(DateTimeDurationPickerValueType.Duration);
    setInputValue(formatDuration(selectedDuration));
  }, [selectedDuration, setEntryMode, setInputValue]);

  const handleDurationChange = useCallback(
    (newDuration: Duration) => {
      _setSelectedDuration(newDuration);
      setInputValue(formatDuration(newDuration));
      setInternalTouched(true);
    },
    [_setSelectedDuration, setInputValue, setInternalTouched]
  );

  const handleDateChange = useCallback(
    (newDate: Date | null) => {
      newDate = isValidDateInput(newDate) ? newDate : null;
      _setSelectedDate(newDate);
      setSelectedTime(newDate ?? selectedTime);
      setInputValue(formattedDate(newDate ?? selectedTime));
      setInternalTouched(true);
    },
    [_setSelectedDate, selectedTime, setInputValue, setInternalTouched, setSelectedTime]
  );

  const setValueFromString = useCallback(
    (newValue: string) => {
      setInternalTouched(true);

      const parsedDate = parseDate(newValue);
      const shouldTreatAsDuration =
        hasDurationEntryMode &&
        (canParseAsDuration(newValue) ||
          (!isValid(parsedDate) && entryMode === DateTimeDurationPickerValueType.Duration));

      if (shouldTreatAsDuration) {
        const newDuration = parseDuration(newValue);
        _setSelectedDuration(newDuration);
        setEntryMode(DateTimeDurationPickerValueType.Duration);
        return { date: null, duration: newDuration };
      }

      if (hasDateTimeEntryMode) {
        setEntryMode(DateTimeDurationPickerValueType.DateTime);
      }

      if (!newValue) {
        _setSelectedDate(null);
        return { date: null, duration: null };
      }

      if (isValidDateInput(parsedDate)) {
        _setSelectedDate(parsedDate);
        setSelectedTime(parsedDate);
      }

      return { date: parsedDate, duration: null };
    },
    [
      setInternalTouched,
      entryMode,
      hasDateTimeEntryMode,
      hasDurationEntryMode,
      _setSelectedDuration,
      setEntryMode,
      _setSelectedDate,
      setSelectedTime,
    ]
  );
  const handleInputTextBoxChange: React.ChangeEventHandler<HTMLInputElement> = useCallback(
    e => {
      setValueFromString(e.target.value);
      setInputValue(e.target.value);
      setInternalTouched(true);
    },
    [setInputValue, setInternalTouched, setValueFromString]
  );

  const handleClickCancel = useCallback(() => {
    if (isDuration(value)) {
      setInputValue(formatDuration(value));
    } else {
      setInputValue(value ? formattedDate(value) : '');
    }
    setIsFocused(false);
    closePopup();
  }, [value, closePopup, setInputValue, setIsFocused]);

  const maybeEmitChange = useCallback(
    (newEntryMode: DateTimeDurationPickerValueType, newDate: Date | null, newDuration: Duration | null) => {
      if (newEntryMode === DateTimeDurationPickerValueType.Duration) {
        if (!isEqual(newDuration, value)) {
          onChange({ type: DateTimeDurationPickerValueType.Duration, value: newDuration ?? emptyDuration });
        }
      } else if (!isEqual(newDate, value)) {
        onChange({ type: DateTimeDurationPickerValueType.DateTime, value: newDate });
      }
    },
    [onChange, value]
  );

  const clearValue = useCallback(() => {
    setInternalTouched(true);
    const { date: newDate, duration: newDuration } = setValueFromString('');
    setInputValue('');
    maybeEmitChange(entryMode, newDate, newDuration);
    setIsFocused(false);
    closePopup();
  }, [setInternalTouched, setValueFromString, setInputValue, maybeEmitChange, entryMode, setIsFocused, closePopup]);

  const handleSelectDay = useCallback(
    (newDate: Date | undefined) => {
      if (!newDate) {
        return;
      }
      setInternalTouched(true);
      // Note that the DayPicker returns a date with time = 12:00:00.000
      if (selectedDate === null) {
        newDate.setHours(customEOD.hours);
        newDate.setMinutes(customEOD.minutes);
        newDate.setSeconds(0);
        newDate.setMilliseconds(0);
      } else {
        newDate.setHours(selectedTime.getHours());
        newDate.setMinutes(selectedTime.getMinutes());
        newDate.setSeconds(selectedTime.getSeconds());
        newDate.setMilliseconds(selectedTime.getMilliseconds());
      }
      if (isValidDateInput(newDate)) {
        setInputValue(formattedDate(newDate));
      } else {
        logger.error(new Error('Received invalid date in date/time/duration picker'), {
          extra: {
            newDate,
            customEOD,
          },
        });
      }
      handleDateChange(newDate);
    },
    [setInternalTouched, selectedDate, handleDateChange, customEOD, selectedTime, setInputValue]
  ) satisfies DayPickerProps['onDayClick'];
  const handleTimeChange = useCallback(
    (newDate: Date) => {
      setInternalTouched(true);
      handleDateChange(newDate);
    },
    [handleDateChange, setInternalTouched]
  );

  const setTimeToEOD = useCallback(() => {
    setInternalTouched(true);
    let newDate = parseDate(selectedDate);
    newDate.setHours(customEOD.hours);
    newDate.setMinutes(customEOD.minutes);
    newDate.setSeconds(0);
    newDate.setMilliseconds(0);
    if (isPast(newDate)) {
      newDate = addDays(newDate, 1);
    }

    handleDateChange(newDate);
  }, [setInternalTouched, selectedDate, customEOD.hours, customEOD.minutes, handleDateChange]);

  const closeAndSetDuration = useCallback(
    (newDuration: Duration) => {
      handleDurationChange(newDuration);
      maybeEmitChange(entryMode, selectedDate, newDuration);
      setIsFocused(false);
      closePopup();
    },
    [entryMode, selectedDate, maybeEmitChange, closePopup, handleDurationChange, setIsFocused]
  );
  const handleClickSet = useCallback(() => {
    if (resolvedIncomingValueState.kind === 'sanitize' && resolvedIncomingValueState.isUnsupportedControlledValue) {
      // Value is controlled but not editable in current entryModes. If user has not edited,
      // treat Set as a no-op to avoid overwriting the controlled value.
      if (!internalTouched) {
        setIsFocused(false);
        closePopup();
        return;
      }
    }
    // Note: we use `selectedTime` here because the time picker defaults to "now" when no value is set.
    // If the user has entered nothing, but clicks Set, we'd want it to set the value they see in the timepicker
    maybeEmitChange(entryMode, selectedTime, selectedDuration);
    setIsFocused(false);
    closePopup();
  }, [
    resolvedIncomingValueState,
    internalTouched,
    entryMode,
    selectedTime,
    selectedDuration,
    maybeEmitChange,
    closePopup,
    setIsFocused,
  ]);

  const onBlur: React.FocusEventHandler<HTMLElement> = useCallback(
    (e: FocusEvent) => {
      if (e.relatedTarget && isFocusOutsideComponent(e.relatedTarget)) {
        if (internalTouched) {
          // if the user has change the value at all, then we'll act as if they clicked "Set" when they blur the control
          handleClickSet();
        } else {
          // Otherwise, if they made no changes, act as if they click cancel
          handleClickCancel();
        }
      }
    },
    [isFocusOutsideComponent, internalTouched, handleClickSet, handleClickCancel]
  );
  const showClear = useMemo(() => inputValue !== '' && !disabled, [disabled, inputValue]);
  // Closes the popover on Escape before parent containers handle it
  const handleKeyDown = useCallback(
    (event: ReactKeyboardEvent<Element>) => {
      if (event.defaultPrevented) {
        return;
      }
      if (event.key !== 'Escape' || !isFocused) {
        return;
      }

      handleClickCancel();
      event.preventDefault();
      event.stopPropagation();
    },
    [handleClickCancel, isFocused]
  );
  const handleInputKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLInputElement>) => {
      onKeyDownProp?.(event);
      if (event.defaultPrevented) {
        return;
      }
      handleKeyDown(event);
    },
    [handleKeyDown, onKeyDownProp]
  );

  return (
    <DateTimeDurationPickerWrapper
      {...props}
      onFocus={handleFocus}
      onBlur={onBlur}
      onKeyDown={handleKeyDown}
      focused={isFocused}
    >
      <Input
        {...props}
        onKeyDown={handleInputKeyDown}
        invalid={invalid}
        warning={warning}
        touched={touched}
        data-testid={dataTestId}
        size={size}
        disabled={disabled}
        value={inputValue}
        onChange={handleInputTextBoxChange}
        ref={inputRef}
        id={id ?? idGenerator('value')}
        aria-describedby={idGenerator('description')}
        aria-label={props['aria-label'] ?? 'Value'}
        suffix={
          <>
            <InputOverlay
              warning={!!warning}
              id={idGenerator('description')}
              role="note"
              aria-label={`${props['aria-label'] ?? 'Value'} Description`}
              size={size}
            >
              {entryMode === DateTimeDurationPickerValueType.Duration ? (
                <>
                  <DurationDescription data-testid="duration-description" disabled={disabled}>
                    {formatDuration(selectedDuration)}
                  </DurationDescription>
                  {showCalculatedDuration &&
                  !isEqual(selectedDuration, emptyDuration) &&
                  isValidDateInput(calcDurationRelativeTo) ? (
                    <DateDescription data-testid="date-description" disabled={disabled}>
                      &nbsp;({formattedDate(calcDateFromDuration(selectedDuration, calcDurationRelativeTo))})
                    </DateDescription>
                  ) : null}
                </>
              ) : isValidDateInput(selectedDate) ? (
                <DateDescription disabled={disabled}>{formattedDate(selectedDate)}</DateDescription>
              ) : null}
            </InputOverlay>
            <IconButton
              ghost
              round
              size={(size ?? FormControlSizes.Default) - 0.5}
              icon={IconName.Close}
              ref={clearButtonRef}
              onClick={clearValue}
              style={{ visibility: showClear ? 'visible' : 'hidden' }}
              tabIndex={-1}
              aria-label="Clear value"
              data-testid="date-time-duration-picker-clear-button"
            />
            <Popover {...popover} tabIndex={-1}>
              <Icon
                icon={IconName.Clock}
                aria-label="Open"
                role="button"
                spaceAfter="spacing4"
                style={{ marginRight: '4px', visibility: disabled ? 'hidden' : 'visible' }}
              />
              <PopoverWrapper ref={popoverRef} onKeyDown={handleKeyDown}>
                <HStack h="100%" w="100%" justifyContent="space-between">
                  {entryMode === DateTimeDurationPickerValueType.DateTime ? (
                    <DateTimeWrapper role="tabpanel" aria-labelledby={idGenerator('date-time')}>
                      <DayPicker
                        animate={true}
                        mode="single"
                        required={false}
                        numberOfMonths={1}
                        onSelect={handleSelectDay}
                        disabled={{ before: parseDate() }}
                        startMonth={parseDate()}
                        /* dates passed to DayPicker must be valid dates or else it crashes */
                        selected={selectedDate && isValid(selectedDate) ? selectedDate : undefined}
                        month={selectedDate && isValid(selectedDate) ? selectedDate : undefined}
                      />
                      <TimePickerWrapper>
                        <TimePicker
                          date={selectedTime}
                          onChange={handleTimeChange}
                          maxPrecision={TimePickerPrecision.SECOND}
                        />
                        <Button onClick={setTimeToEOD}>EOD</Button>
                      </TimePickerWrapper>
                    </DateTimeWrapper>
                  ) : (
                    <DurationPicker
                      value={selectedDuration}
                      onChange={handleDurationChange}
                      closeAndSetValue={closeAndSetDuration}
                      showShortcuts={showShortcuts}
                      shortcuts={shortcuts}
                      role="tabpanel"
                      aria-labelledby={idGenerator('duration')}
                      id={idGenerator('duration-picker')}
                    />
                  )}
                  <VStack
                    justifyContent="stretch"
                    alignSelf="stretch"
                    p="spacing12"
                    style={{ background: theme.backgroundPopoverHighlight }}
                  >
                    {entryModes.length > 1 && (
                      <ButtonGroup orientation="vertical" role="tablist">
                        {entryModes.includes(DateTimeDurationPickerValueType.DateTime) && (
                          <ToggleButton
                            selected={entryMode === DateTimeDurationPickerValueType.DateTime}
                            onClick={setDateTimeEntryMode}
                            style={{ width: '100%' }}
                            role="tab"
                            aria-selected={entryMode === DateTimeDurationPickerValueType.DateTime}
                            id={idGenerator('date-time')}
                          >
                            {dateTimeLabel}
                          </ToggleButton>
                        )}
                        {entryModes.includes(DateTimeDurationPickerValueType.Duration) && (
                          <ToggleButton
                            selected={entryMode === DateTimeDurationPickerValueType.Duration}
                            onClick={setDurationEntryMode}
                            style={{ width: '100%' }}
                            role="tab"
                            aria-selected={entryMode === DateTimeDurationPickerValueType.Duration}
                            id={idGenerator('duration')}
                          >
                            {durationLabel}
                          </ToggleButton>
                        )}
                      </ButtonGroup>
                    )}
                    <Flex flex="1" h="100%" />
                    <Button
                      variant={ButtonVariants.Default}
                      style={{ width: '100%', marginBottom: theme.spacing8 + 'px' }}
                      onClick={handleClickCancel}
                    >
                      Cancel
                    </Button>
                    <Button data-testid="duration-submit-button" style={{ width: '100%' }} onClick={handleClickSet}>
                      Set
                    </Button>
                  </VStack>
                </HStack>
              </PopoverWrapper>
            </Popover>
          </>
        }
      />
    </DateTimeDurationPickerWrapper>
  );
});
